import { ImageScaleView, View, animate } from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import StateObserver, { now, user } from 'src/StateObserver';
import PopupMatch2Base from '../components/PopupMatch2Base';
import { MagicLampsLampSet } from './MagicLampsLampSet';
import { MagicLampsGrandPrize } from './MagicLampsGrandPrize';
import { MagicLampsProgressBar } from './MagicLampsProgressBar';
import { MagicLampsRewards } from './MagicLampsRewards';
import { MagicLampsMessage } from './MagicLampsMessage';
import {
  magicLampsAnim,
  magicLampsAnimClear,
  magicLampsAnimDelay,
} from './MagicLampsAnim';
import {
  magicLampsGetAccumulatedRewards,
  magicLampsGetContinueCost,
  magicLampsGetCurrentRound,
  magicLampsGetCurrentStage,
  magicLampsGetEntryCost,
  magicLampsGetGrandPrize,
  magicLampsGetPreviousStage,
  magicLampsGetRemainingTime,
  magicLampsGetStages,
  magicLampsHasGameInProgress,
  magicLampsIsCompleted,
} from 'src/replicant/match2/magiclamps/magiclamps.getters';
import {
  magicLampsContinue,
  magicLampsPlay,
  magicLampsPromptCollect,
  magicLampsPromptQuit,
} from './magiclamps.sequence';
import { MagicLampsCostButton } from './MagicLampsCostButton';
import { MagicLampsRewardId } from 'src/replicant/match2/magiclamps/magiclamps.ruleset';
import { MagicLampsRewardIcon } from './MagicLampsRewardIcon';
import { MagicLampsInfo } from './MagicLampsInfo';
import { magicLampsPlaySfx } from './magiclamps.utils';
import { getRandomInt } from 'src/replicant/utils/random';
import { Match2Timer } from '../components/Match2Timer';

export default class PopupMatch2MagicLamps extends PopupMatch2Base {
  private stall: ImageScaleView;
  private ribbon: ImageScaleView;
  private progressBar: MagicLampsProgressBar;
  private lamps: MagicLampsLampSet;
  private grandPrize: MagicLampsGrandPrize;
  private info: MagicLampsInfo;
  private rewards: MagicLampsRewards;
  private message: MagicLampsMessage;
  private buttonStart: MagicLampsCostButton;
  private buttonContinue: MagicLampsCostButton;
  private buttonCollect: MagicLampsCostButton;
  private timer: Match2Timer;
  private close?: (success: boolean) => void;
  public playing = false;

  /** Instance of this popup, for cheats */
  public static instance?: PopupMatch2MagicLamps;

  constructor(opts: { superview: View; close: (success: boolean) => void }) {
    const boxWidth = 600;
    const boxHeight = 1017;
    const locale = StateObserver.getLocale() === 'en' ? 'en' : 'ja';

    super({
      superview: opts.superview,
      panelImageOpts: {
        image: `assets/ui/match2/magiclamps/magiclamps_panel.png`,
        width: boxWidth,
        height: boxHeight,
        imgScale: 0.66,
        scaleMethod: '9slice',
        sourceSlices: {
          horizontal: { left: 90, right: 90 },
          vertical: { top: 90, bottom: 660 },
        },
      },
      offsetY: -250,
      close: () => this.quit(),
      title: {
        type: 'image',
        opts: {
          scaleMethod: 'contain',
          canHandleEvents: false,
          image: `assets/ui/match2/magiclamps/magiclamps_title_${locale}.png`,
          width: 464,
          height: 99,
          y: 10,
        },
      },
      tracking: {
        feature: Feature.MATCH2.MAGIC_LAMPS._,
        $subFeature: Feature.MATCH2.MAGIC_LAMPS.POPUP,
      },
    });

    PopupMatch2MagicLamps.instance = this;

    this.close = opts.close;

    this.buttonStart = new MagicLampsCostButton({
      superview: this.panel,
      visible: false,
      scale: 0,
      onClick: async () => {
        magicLampsPlaySfx('ok');
        this.play();
      },
      centerOnOrigin: true,
      x: this.panel.style.width * 0.5,
      y: 935,
      width: 300,
      tracking: {
        feature: Feature.MATCH2.MAGIC_LAMPS._,
        $subFeature: Feature.MATCH2.MAGIC_LAMPS.START,
      },
    });

    this.buttonContinue = new MagicLampsCostButton({
      superview: this.panel,
      visible: false,
      scale: 0,
      onClick: async () => {
        magicLampsPlaySfx('ok');
        this.continue();
      },
      centerOnOrigin: true,
      x: this.panel.style.width * 0.5,
      y: 935,
      tracking: {
        feature: Feature.MATCH2.MAGIC_LAMPS._,
        $subFeature: Feature.MATCH2.MAGIC_LAMPS.CONTINUE,
      },
    });

    this.buttonCollect = new MagicLampsCostButton({
      visible: false,
      scale: 0,
      fontSize: 40,
      labelOffsetY: -2,
      font: 'Body',
      width: 300,
      superview: this.panel,
      onClick: async () => {
        magicLampsPlaySfx('ok');
        this.collect();
      },
      centerOnOrigin: true,
      x: this.panel.style.width * 0.5,
      y: 935,
      tracking: {
        feature: Feature.MATCH2.MAGIC_LAMPS._,
        $subFeature: Feature.MATCH2.MAGIC_LAMPS.COLLECT,
      },
    });

    this.buttonClose!.updateOpts({ y: 3, visible: false });

    this.ribbon = new ImageScaleView({
      superview: this.panel,
      image: `assets/ui/match2/magiclamps/magiclamps_ribbon.png`,
      width: 540,
      height: 106,
      x: this.panel.style.width * 0.5,
      y: 8,
      centerOnOrigin: true,
      centerAnchor: true,
      zIndex: -1,
    });

    this.stall = new ImageScaleView({
      superview: this.panel,
      image: `assets/ui/match2/magiclamps/magiclamps_stall.png`,
      width: 665,
      height: 605,
      centerOnOrigin: true,
      x: boxWidth * 0.5,
      y: boxHeight - 650,
    });

    this.progressBar = new MagicLampsProgressBar({
      superview: this.stall,
      x: 40,
      y: 50,
      round: 1,
      numRounds: 30,
      checkpoints: magicLampsGetStages(user()),
    });

    this.message = new MagicLampsMessage({
      superview: this.panel,
      width: this.panel.style.width,
      height: this.panel.style.height,
    });

    this.lamps = new MagicLampsLampSet({
      superview: this.stall,
      x: this.stall.style.width * 0.5,
      y: this.stall.style.height - 80,
      amount: 4,
      onPick: this.playRound.bind(this),
    });
    this.lamps.hide();

    this.grandPrize = new MagicLampsGrandPrize({
      superview: this.stall,
      x: this.stall.style.width - 100,
      y: 90,
      scale: 0.75,
      amount: magicLampsGetGrandPrize(),
    });

    this.rewards = new MagicLampsRewards({
      superview: this.panel,
      x: 50,
      y: 670,
    });

    this.info = new MagicLampsInfo({
      superview: this.panel,
      width: this.panel.style.width,
      height: this.panel.style.height,
    });

    this.timer = new Match2Timer({
      superview: this.panel,
      centerOnOrigin: true,
      x: this.panel.style.width * 0.5,
      y: 645,
    });
    this.timer.setRemainingTimeGetter(magicLampsGetRemainingTime);
  }

  public init() {
    super.init({});
    const cost = magicLampsGetEntryCost(user());
    this.buttonStart.setup({
      i18n: 'match2.magicLamps.start',
      gems: cost,
    });
    this.buttonCollect.setup({
      i18n: 'match2.magicLamps.collect',
    });
  }

  public override async fadeIn(): Promise<void> {
    this.setInteractivity(false);
    this.timer.autoUpdate();
    this.panel.updateOpts({ scaleY: 0 });
    this.stall.updateOpts({ scale: 0 });
    this.ribbon.updateOpts({ scaleX: 0 });
    this.title?.updateOpts({ scale: 0 });
    this.timer.updateOpts({ scale: 0 });
    super.fadeIn();
    await magicLampsAnimDelay(150);
    magicLampsAnim(this.panel, { scaleY: 1 }, 300, animate.easeOutBack);
    await magicLampsAnimDelay(150);
    magicLampsAnim(this.stall, { scale: 1 }, 300, animate.easeOutBack);
    await magicLampsAnimDelay(150);
    magicLampsAnim(this.ribbon, { scaleX: 1 }, 300, animate.easeOutBack);
    await magicLampsAnimDelay(150);
    this.buttonClose?.show();
    if (this.title) {
      magicLampsAnim(this.title, { scale: 1 }, 300, animate.easeOutBack);
    }
    magicLampsAnim(this.timer, { scale: 1 }, 300, animate.easeOutBack);

    // Resume game if user has already started playing.
    if (magicLampsHasGameInProgress(user(), now())) {
      await this.play(true);
    }
    // Otherwise show info screen.
    else {
      await this.showInfo();
    }

    this.setInteractivity(true);
  }

  public override async fadeOut(): Promise<void> {
    this.playing = false;
    PopupMatch2MagicLamps.instance = undefined;
    this.grandPrize.unshineAnimated();
    this.timer.stopAutoUpdate();
    // Interrupt all magic lamps animation
    magicLampsAnimClear();
    return super.fadeOut();
  }

  private async showInfo() {
    this.lamps.hide();
    await this.info.showAnimated();
    await this.buttonStart.showAnimated();
  }

  private async play(resume = false) {
    this.setInteractivity(false);
    this.buttonStart.hideAnimated();
    this.info.hideAnimated();

    if (!resume) {
      // Play magic lamps if user has enough gems to pay for the entry cost
      const result = await magicLampsPlay();
      if (!result) {
        this.buttonStart.showAnimated();
        this.setInteractivity(true);
        this.info.showAnimated();
        return;
      }
    }

    this.playing = true;
    this.timer.stopAutoUpdate();
    this.timer.setMessage(i18n('match2.magicLamps.yourRewards'));

    const rewards = magicLampsGetAccumulatedRewards(user());
    this.rewards.updateRewards(rewards);
    await this.progressBar.animateToNextRound(
      magicLampsGetCurrentRound(user()),
    );
    await this.updateStage();
    await this.lamps.lampsJumpIn();

    if (resume && user().match2.magicLamps?.trap) {
      const randomLamp = getRandomInt(0, this.lamps.length - 1, Math.random);
      const lamp = this.lamps.getLamp(randomLamp);
      lamp.setPrize({ type: 'trap', value: 1 });
      await lamp.open();
      await this.showLost();
    } else {
      if (Object.values(rewards).some((x) => x > 0)) {
        this.buttonCollect.showAnimated();
      }
      await this.message.showMessage('pick');
      this.lamps.enablePick();
    }
    this.setInteractivity(true);
  }

  private async nextRound(round: number) {
    this.message.hideMessage();
    this.rewards.fullColor();
    await this.lamps.lampsReset();
    await this.lamps.lampsJumpOut();
    await this.progressBar.animateToNextRound(round);
    await this.updateStage();
    await this.lamps.lampsJumpIn();
    this.buttonCollect.showAnimated();
    this.message.showMessage('pick');
    this.lamps.enablePick();
  }

  private async playRound(selectedLamp: number) {
    // Disable interactivity, to prevent collect during animation
    this.setInteractivity(false);

    this.lamps.disablePick();
    this.message.hideMessage();

    // Call the action promise in advance, as it might have some delay
    const actionPromise = StateObserver.invoke.magicLampsPlayRound();

    // Animate selected lamp
    await this.lamps.highlighLamp(selectedLamp);

    // Await the result of the action promise before continuing
    const result = await actionPromise;

    if (!result) {
      // Something has gone wrong if results are undefined
      // leave to the backend to handle and let user try again.
      await this.lamps.lampsReset();
      this.lamps.enablePick();
      this.setInteractivity(true);
      return;
    }

    // Set the prize selection to the lamps
    this.lamps.lampsSetPrizes(selectedLamp, result.prizes.slice());

    // Reveal lamps
    if (!result.trap) {
      magicLampsPlaySfx('win', { volume: 0.4 });
    } else {
      magicLampsPlaySfx('attack-lose');
    }
    await this.lamps.lampsReveal(selectedLamp);

    // Restore interactivity
    this.setInteractivity(true);

    if (result.completed) {
      // User got a reward and completed the event
      await this.showWin(selectedLamp);
      await this.showComplete();
    } else if (result.trap) {
      // User got a trap and lost the round
      await this.showLost();
    } else {
      // User got a reward and won the round
      await this.showWin(selectedLamp);
      this.nextRound(magicLampsGetCurrentRound(user()));
    }
  }

  private async showWin(selectedLamp: number) {
    await magicLampsAnimDelay(1200);
    await this.animateReward(selectedLamp);
  }

  private async showLost() {
    const continueCost = magicLampsGetContinueCost(user());
    this.buttonCollect.hideAnimated();
    await this.rewards.greyOut();
    await this.message.showMessage('foundGenie');
    this.buttonContinue.setup({
      i18n: 'match2.magicLamps.continue',
      gems: continueCost,
    });
    this.buttonContinue.showAnimated();
  }

  private async showComplete() {
    this.buttonClose?.hide();
    magicLampsPlaySfx('rewards-flying');
    await this.progressBar.animateComplete();
    await this.grandPrize.shineAnimated();
    await this.lamps.lampsReset();
    await this.lamps.lampsJumpOut();
    await this.message.showMessage('congratulations');
    magicLampsPlaySfx('applause');
    this.buttonCollect.showAnimated();
  }

  private async continue() {
    this.buttonContinue.hideAnimated();
    const success = await magicLampsContinue();
    if (!success) {
      this.buttonContinue.showAnimated();
      return;
    }
    this.message.hideMessage();
    await this.lamps.removeTrap();
    this.rewards.fullColor();
    this.buttonCollect.showAnimated();
    this.message.showMessage('pick');
    this.lamps.enablePick();
  }

  private async collect() {
    if (!magicLampsIsCompleted(user())) {
      const confirm = await magicLampsPromptCollect();
      if (!confirm) return;
    }
    this.buttonCollect.hideAnimated();
    this.close?.(true);
  }

  private async quit() {
    if (this.playing) {
      const confirm = await magicLampsPromptQuit();
      if (!confirm) return;
    }
    this.close?.(false);
  }

  private async updateStage() {
    const previousStage = magicLampsGetPreviousStage(user());
    const currentStage = magicLampsGetCurrentStage(user());
    this.message.setStage(currentStage);
    this.lamps.setStage(currentStage);

    if (previousStage !== currentStage) {
      magicLampsPlaySfx('gachaItemDrop', { volume: 0.3 });
      this.message.showMessage('stage');
      await magicLampsAnimDelay(1500);
      await this.message.hideMessage();
    }
  }

  private async animateReward(selectedLamp: number) {
    const rewards = magicLampsGetAccumulatedRewards(user());
    this.rewards.addRewards(rewards);

    const lamp = this.lamps.getLamp(selectedLamp);
    if (!lamp.prize || !lamp.prizeId || lamp.prizeId === 'trap') return;
    const movingIcon = lamp.prize as MagicLampsRewardIcon;
    const targetIcon = this.rewards.getIconByType(
      lamp.prizeId as MagicLampsRewardId,
    );

    const fromPos = movingIcon.getPosition(this.panel);
    const toPos = targetIcon.getPosition(this.panel);

    movingIcon.reparent(this.panel);
    movingIcon.updateOpts({
      x: fromPos.x,
      y: fromPos.y,
    });

    const duration = 500;

    magicLampsAnim(
      movingIcon.style,
      { x: toPos.x, y: toPos.y },
      duration,
      animate.easeInQuad,
    );

    await magicLampsAnim(
      movingIcon,
      { scale: 2 },
      duration * 0.5,
      animate.easeOutQuad,
    );

    await magicLampsAnim(
      movingIcon,
      { scale: 1 },
      duration * 0.5,
      animate.easeInQuad,
    );

    magicLampsPlaySfx('sfx-star1');
    this.rewards.updateRewards(rewards);
    lamp.prize.removeFromSuperview();
  }

  /** Used by cheats only */
  public refresh() {
    this.progressBar.animateToNextRound(magicLampsGetCurrentRound(user()));
    this.updateStage();
    this.rewards.updateRewards(magicLampsGetAccumulatedRewards(user()));
  }
}
